import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { BookOpen, Share2, X, MessageCircle, Mail, Copy, HandHeart, Heart, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import SnowLayer from "../components/SnowLayer";
import { Loader } from "../components/shared";
import { Modal } from "../components/Modal";
import BackToHome from "../components/BackToHome";

export default function Moment() {
  const { t, lang } = useI18n();
  const [prayer, setPrayer] = useState(null);
  const [gratitude, setGratitude] = useState(null);
  const [choice, setChoice] = useState(null); // null | prayer | gratitude
  const [reading, setReading] = useState(false);
  const [share, setShare] = useState(false);

  useEffect(() => {
    api.get(`/content/prayer?lang=${lang}`).then(({ data }) => setPrayer(data)).catch(() => {});
    api.get(`/christmas/gratitude?lang=${lang}`).then(({ data }) => setGratitude(data)).catch(() => {});
  }, [lang]);

  useEffect(() => {
    document.body.style.overflow = reading ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [reading]);

  if (!prayer || !gratitude) return <Loader />;
  const data = choice === "prayer" ? prayer : choice === "gratitude" ? gratitude : null;
  const image = prayer.image;

  // ---- Chooser ----
  if (!choice) {
    return (
      <div className="relative -mx-4 -my-6 overflow-hidden sm:-mx-6 sm:-my-8" data-testid="moment-page">
        <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-xmas-navy/80 via-xmas-navy/90 to-xmas-navy" />
        <SnowLayer count={22} />
        <div className="relative z-10 mx-auto max-w-3xl px-6 py-16 sm:py-24">
          <BackToHome />
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-xmas-gold">{t("moment.section_title")}</p>
            <p className="mx-auto mt-5 max-w-xl font-serif-elegant text-xl italic leading-relaxed text-xmas-champagne sm:text-2xl">{t("moment.choose")}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <ExperienceCard testid="choose-prayer" icon={HandHeart} title={prayer.title} desc={t("moment.prayer_desc")} badge={prayer.reading_time} onClick={() => setChoice("prayer")} t={t} />
            <ExperienceCard testid="choose-gratitude" icon={Heart} title={gratitude.title} desc={t("moment.gratitude_desc")} badge={gratitude.reading_time} onClick={() => setChoice("gratitude")} t={t} />
          </div>
        </div>
      </div>
    );
  }

  const paragraphs = data.paragraphs || [];
  const buildText = () => `${data.title}\n\n${paragraphs.join("\n\n")}`;
  const shareWhatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(buildText())}`, "_blank");
  const shareEmail = () => { window.location.href = `mailto:?subject=${encodeURIComponent(data.title)}&body=${encodeURIComponent(buildText())}`; };
  const copyText = async () => { try { await navigator.clipboard.writeText(buildText()); toast.success(t("share.copied")); } catch { toast.error("Error"); } };

  return (
    <div className="relative -mx-4 -my-6 overflow-hidden sm:-mx-6 sm:-my-8" data-testid={`moment-${choice}`}>
      <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="absolute inset-0 bg-gradient-to-b from-xmas-navy/80 via-xmas-navy/90 to-xmas-navy" />
      <SnowLayer count={22} />
      <div className="relative z-10 mx-auto max-w-2xl px-6 py-16 sm:py-20">
        <BackToHome />
        <button onClick={() => setChoice(null)} data-testid="moment-back" className="mb-8 inline-flex items-center gap-1 text-sm text-xmas-slate hover:text-xmas-snow"><ArrowLeft className="h-4 w-4" /> {t("moment.back_choose")}</button>
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold leading-tight text-xmas-gold sm:text-4xl" data-testid="moment-title">{data.title}</h1>
          <p className="mt-3 text-base text-xmas-snow/80">{data.subtitle}</p>
          <span className="mt-4 inline-block rounded-full border border-xmas-gold/30 px-4 py-1.5 text-xs uppercase tracking-widest text-xmas-champagne" data-testid="moment-reading-time">{data.reading_time}</span>
        </div>
        <div className="mt-10 space-y-6 text-left" data-testid="moment-text">
          {paragraphs.map((p, i) => <p key={i} className="text-lg leading-loose text-xmas-snow/90 sm:text-xl">{p}</p>)}
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <button onClick={() => setReading(true)} data-testid="reading-mode-button" className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold uppercase tracking-wider"><BookOpen className="h-5 w-5" /> {t("moment.reading_mode")}</button>
          <button onClick={() => setShare(true)} data-testid="share-button" className="glass inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-xmas-snow transition hover:border-xmas-gold/60"><Share2 className="h-4 w-4" /> {t("cta.share")}</button>
        </div>
      </div>

      {reading && createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[#05070f]" data-testid="reading-mode">
          <button onClick={() => setReading(false)} data-testid="reading-exit-button" className="fixed right-4 top-4 z-10 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white backdrop-blur transition hover:bg-white/20"><X className="h-5 w-5" /> {t("moment.exit_reading")}</button>
          <div className="mx-auto max-w-3xl px-6 py-20">
            <h1 className="text-center font-display text-4xl font-bold text-[#F5D66E] sm:text-5xl">{data.title}</h1>
            <div className="mt-12 space-y-8">{paragraphs.map((p, i) => <p key={i} className="text-2xl leading-loose text-white sm:text-3xl sm:leading-loose">{p}</p>)}</div>
          </div>
        </div>, document.body)}

      <Modal open={share} onClose={() => setShare(false)} title={t("cta.share")} testid="moment-share-modal">
        <div className="space-y-3">
          <button onClick={shareWhatsapp} data-testid="share-whatsapp" className="flex w-full items-center gap-3 rounded-xl border border-xmas-gold/20 bg-xmas-navy/40 px-4 py-3 text-left text-sm text-xmas-snow transition hover:border-xmas-gold"><MessageCircle className="h-5 w-5 text-[#25D366]" /> {t("share.whatsapp")}</button>
          <button onClick={shareEmail} data-testid="share-email" className="flex w-full items-center gap-3 rounded-xl border border-xmas-gold/20 bg-xmas-navy/40 px-4 py-3 text-left text-sm text-xmas-snow transition hover:border-xmas-gold"><Mail className="h-5 w-5 text-xmas-gold" /> {t("share.email")}</button>
          <button onClick={copyText} data-testid="share-copy" className="flex w-full items-center gap-3 rounded-xl border border-xmas-gold/20 bg-xmas-navy/40 px-4 py-3 text-left text-sm text-xmas-snow transition hover:border-xmas-gold"><Copy className="h-5 w-5 text-xmas-gold" /> {t("share.copy")}</button>
        </div>
      </Modal>
    </div>
  );
}

function ExperienceCard({ icon: Icon, title, desc, badge, onClick, testid, t }) {
  return (
    <button onClick={onClick} data-testid={testid} className="card-lift group flex flex-col items-center gap-3 rounded-3xl border border-xmas-gold/20 bg-xmas-navycard/70 p-8 text-center transition hover:border-xmas-gold/60">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-xmas-gold/15"><Icon className="h-7 w-7 text-xmas-gold" /></div>
      <h3 className="font-display text-xl text-xmas-snow">{title}</h3>
      <p className="text-sm text-xmas-slate">{desc}</p>
      <span className="rounded-full border border-xmas-gold/25 px-3 py-1 text-[11px] uppercase tracking-widest text-xmas-champagne">{badge}</span>
      <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-xmas-gold">{t("moment.open")} →</span>
    </button>
  );
}
